import type { KingdomId } from '../data/kingdoms';
import type { SkillId } from './skills';

/**
 * Modificadores que un item puede aplicar a las entidades
 */
export interface ItemModifiers {
  minDamage?: number;
  maxDamage?: number;
  physDef?: number;
  magicDef?: number;
  hp?: number;
  mana?: number;
  moveSpeed?: number;
  attackSpeed?: number;
  footstepDuration?: number; // ms flat que se suman a la duración de las huellas en el piso
  critChance?: number;
  critMultiplier?: number;
  evasion?: number;
  castSpeed?: number;
  stamina?: number;
  blockChance?: number; // Probabilidad (decimal, 0.1 = 10%) de bloquear golpes físicos
  manaReduction?: number; // Reducción (decimal, 0.1 = -10%) del costo de maná de hechizos
  regenHP?: number;
  regenMana?: number;
  regenStamina?: number;
  
  // Modificadores porcentuales (se aplican después de los modificadores flat)
  // Los valores son porcentajes como decimal (ej: 0.25 = +25%, 0.1 = +10%)
  hpPercent?: number; // Porcentaje de aumento de HP máximo (ej: 0.25 = +25%)
  manaPercent?: number; // Porcentaje de aumento de Mana máximo (ej: 0.25 = +25%)
  staminaPercent?: number; // Porcentaje de aumento de Stamina máximo (ej: 0.25 = +25%)
  damagePercent?: number; // Porcentaje de aumento de daño (ej: 0.15 = +15% a minDamage y maxDamage)
  attackSpeedPercent?: number; // Porcentaje de aumento de velocidad de ataque (ej: 0.1 = +10%)
  moveSpeedPercent?: number; // Porcentaje de aumento de velocidad de movimiento (ej: 0.1 = +10%)
  footstepDurationPercent?: number; // Porcentaje de aumento de duración de las huellas (ej: 0.5 = +50%)
  critChancePercent?: number; // Porcentaje adicional de chance de crítico (ej: 0.05 = +5%)
  critMultiplierPercent?: number; // Porcentaje adicional de multiplicador de crítico (ej: 0.1 = +10%)
  /**
   * Reduce el BONUS de daño crítico por esta fracción (0..1). Escala el extra por
   * encima de 1× que aporta el critMultiplier final: 0.5 = la mitad del extra (ej.
   * de 1.5× a 1.25×); 1.0 = el crítico pega como un golpe normal (1.0×). Se aplica
   * dinámicamente sobre el critMultiplier ya computado en calculateFinalAttributes,
   * así respeta cualquier otra fuente de crit. Trade-off típico de builds/ítems que
   * regalan críticos garantizados (backstab). Ver docs/COMBAT_SYSTEMS.md.
   */
  critDamageReductionPercent?: number;

  // ─── Radio de ataque (melee direccional) ────────────────────────────────
  // Bonos al Radio de ataque. A diferencia de attackWidth/Length/Back del arma
  // (intrínsecos, no se suman), estos SÍ se acumulan de items/afijos/buffs/sets/
  // raza/clase y se aplican sobre la base. Ver docs/COMBAT_SYSTEMS.md.
  /** Pasos laterales extra: +1 agrega 1 tile a CADA costado (ancho sigue impar). */
  attackRangeWidth?: number;
  /** Tiles extra hacia adelante (largo). El largo final se clampea a 1..5. */
  attackRangeLength?: number;
  /** Tiles extra hacia atrás. El final se clampea a 0..1. */
  attackRangeBack?: number;

  // ─── Daño por CATEGORÍA (porcentuales, decimal: 0.15 = +15%) ────────────
  // Se aplican multiplicativos sobre el daño final de su categoría. La categoría
  // física la define el SUBTIPO del arma equipada (BOW/CROSSBOW/THROWABLE →
  // proyectil; el resto, incluido unarmed → melee). Fuente típica: barcos.
  // Ver docs/WATER_AND_BOATS.md y docs/COMBAT_SYSTEMS.md.
  meleeDamagePercent?: number; // daño físico con armas melee (o sin arma)
  projectileDamagePercent?: number; // daño físico con arco/ballesta/arrojadiza
  spellDamagePercent?: number; // daño de hechizos (SpellSystem.processDamageEffect)

  // ─── Daño por REINO de criatura (dinámico) ───────────────────────────────
  // +% de daño (decimal, 0.12 = +12%) contra criaturas de un Reino, aplicado a
  // CUALQUIER escuela (melee/proyectil/hechizo). Estructura anidada por Reino: es
  // dinámica (un Reino nuevo entra al Record sin declarar keys). La producen los
  // afijos generados `aff_dmg_vs_<kingdom>` y se consume en BaseEntity.calculateDamage
  // / SpellSystem.processDamageEffect leyendo el Reino del target. Ver
  // docs/CREATURE_KINGDOMS.md.
  damageVsKingdom?: Partial<Record<KingdomId, number>>;

  // ─── Magic Find por REINO de criatura (dinámico) ─────────────────────────
  // +% de chance de drops RARE/EPIC (decimal) SÓLO al matar criaturas de ese Reino.
  // Se SUMA al `magicFind` global en el roll de rareza cuando el mob muerto pertenece
  // al Reino. Misma estructura anidada/dinámica que `damageVsKingdom`. La producen
  // los afijos `aff_mf_vs_<kingdom>` y se consume en GameRoom.dropLootForNpc. Ver
  // docs/CREATURE_KINGDOMS.md.
  magicFindVsKingdom?: Partial<Record<KingdomId, number>>;

  // ─── Skilling: XP y efectividad por skill (dinámico por skill) ───────────
  // Mapas anidados por SkillId (misma mecánica que magicFindVsKingdom). Permiten
  // armar "sets de skilling". Porcentajes DECIMALES como el resto de ItemModifiers
  // (0.10 = +10%).
  //   skillXpBonus:       +% de XP ganada en esa skill (minería, combate, etc.).
  //   skillEffectiveness: +% de efectividad en esa skill; el efecto concreto lo
  //                       define cada skill (ej. minería: chance de mena extra).
  // Se deep-mergean vía addModifiersInto (NESTED_SKILL_KEYS en shared/utils/modifiers.ts)
  // y NO entran al pipeline de combate: los lee el agregador de skilling sobre el
  // equipo del player. Ver docs/SKILLS_SYSTEM.md.
  skillXpBonus?: Partial<Record<SkillId, number>>;
  skillEffectiveness?: Partial<Record<SkillId, number>>;

  // ─── Backstab: crítico garantizado por la espalda, POR TIPO de arma ──────
  // Mapa anidado por subtipo de arma (flag >0 = habilitado para ese subtipo). Si
  // el portador golpea a un objetivo por su espalda (mismo heading atacante/objetivo)
  // con un arma cuyo subtipo esté habilitado acá, el golpe melee es crítico 100%
  // (BaseEntity.calculateDamage). Estructura anidada/dinámica igual que damageVsKingdom:
  // un ítem/keystone declara p.ej. { dagger: 1 } o { axe: 1 }. Se deep-mergea vía
  // addModifiersInto (NESTED_MAP_KEYS). Sólo melee (los hechizos no pasan por
  // calculateDamage). Ver docs/COMBAT_SYSTEMS.md.
  backstabCrit?: Partial<Record<WeaponSubtype, number>>;

  // ─── Daño +% por TIPO de arma equipada (dinámico) ────────────────────────
  // Mapa anidado por subtipo de arma (decimal, 0.15 = +15%). Multiplica el daño FÍSICO
  // del portador cuando empuña un arma de ese subtipo (BaseEntity.calculateDamage). Lo
  // producen los afijos rolled `aff_dmg_with_<subtype>` (T1/T2/T3, en armas y armaduras) y
  // nodos del árbol. Sólo subtipos físicos (no staff/wand). Se deep-mergea vía
  // addModifiersInto (NESTED_WEAPON_KEYS). Ver docs/AFFIXES_AND_TIERS.md.
  damageWithWeapon?: Partial<Record<WeaponSubtype, number>>;

  // ─── Alcance de hechizos ─────────────────────────────────────────────────
  // Tiles flat que se suman al RANGO MÁXIMO de un hechizo y se restan (con clamp
  // a 0) de su RANGO MÍNIMO. Fuente típica: afijo `aff_spell_range` (3 tiers) y
  // modifiers innatos de raza/clase. Lo consume SpellSystem.checkSpellRange.
  // Ver docs/SPELLS.md.
  spellRangeBonus?: number;

  /**
   * Bonus a la skill de PESCA (decimal). PLACEHOLDER — bloqueado por el sistema
   * **Skill de Pesca** (aún inexistente): el modifier se transporta y muestra
   * "(próximamente)" pero no tiene wiring. El checklist de qué destapar al
   * construir la Pesca está en docs/FISHING_SKILL.md (ver tb docs/WATER_AND_BOATS.md
   * para el gateo por agua / barco pesquero).
   */
  fishingBonus?: number;

  /**
   * Reducción de la probabilidad de ROTURA de herramientas (decimal, 0.1 = -10%).
   * Se acumula sumando (equipo + buffs) y se lee en el roll de rotura del gathering
   * (GameRoom.handleGatherResourceMessage): breakChance efectivo = breakChance base
   * × (1 − clamp(toolBreakReduction, 0, 1)). Fuente típica: buff de comida
   * ("Bien Alimentado" de durabilidad). Ver docs/FISHING_SKILL.md / plan de Comida.
   */
  toolBreakReduction?: number;

  // ─── Mods de MINERÍA (skiller) — sólo tienen efecto al minar; se leen en
  //     GameRoom.tryGatherOnce vía player.getModifiers(). Numéricos planos (0..1).
  //     Fuente: picos únicos equipados + nodo del Enano. Ver docs/MINING_AND_SMITHING.md.
  /** +chance ADITIVA de que aflore una GEMA al minar (se suma al % base por tier de gemDropChance). */
  miningGemChanceBonus?: number;
  /** Chance de que un intento exitoso NO consuma el yacimiento (veta rica). Sólo minería (pickaxe). */
  miningNoDepleteChance?: number;
  /**
   * Bonus MULTIPLICATIVO al % de éxito del yacimiento: éxito efectivo =
   * clamp(node.successChance × (1 + este valor), 0, 1). 1.0 = duplica (nodo grande del Enano).
   */
  miningSuccessChanceBonus?: number;

  // Robo de vida (life steal) - porcentaje del daño que se convierte en HP
  lifeStealPhysical?: number; // Porcentaje (ej: 0.05 = 5% del daño físico)
  lifeStealMagic?: number; // Porcentaje (ej: 0.05 = 5% del daño mágico)

  // Robo de maná (mana leech) - porcentaje del daño infligido que se recupera
  // como maná del atacante. Proc en applyDamageWithHooks (tras el lifesteal).
  manaLeech?: number; // Porcentaje (ej: 0.05 = 5% del daño → maná)
  
  // Reflejar daño recibido - porcentaje del daño que se refleja al atacante
  reflectPhysicalDamage?: number; // Porcentaje (ej: 0.1 = 10% del daño físico recibido)
  reflectMagicDamage?: number; // Porcentaje (ej: 0.1 = 10% del daño mágico recibido)
  
  // Modificadores de curación
  healingOutput?: number; // Porcentaje de aumento a curación que haces a otros (ej: 0.2 = +20%)
  healingReceived?: number; // Porcentaje de aumento a curación que recibes (ej: 0.2 = +20%)

  // ─── Pérdida de movilidad (Inmovilizar / Paralizar) ──────────────────────
  // Procs ofensivos: chance (decimal, ej 0.1 = 10%) de aplicar el efecto al
  // golpear. Hay 4 canales separados (físico/mágico × inmovilizar/paralizar) por
  // rigurosidad. El tier del afijo escala chance Y duración (la duración se
  // resuelve en loadPlayerEquipment vía AffixDefinition.tierDurationsMs). Ver
  // docs/CC_IMMOBILIZE_PARALYZE.md.
  immobilizeOnHitPhysical?: number; // chance de inmovilizar con golpe melee/físico
  immobilizeOnHitSpell?: number; // chance de inmovilizar con daño de hechizo
  paralyzeOnHitPhysical?: number; // chance de paralizar con golpe melee/físico
  paralyzeOnHitSpell?: number; // chance de paralizar con daño de hechizo
  // Reducción defensiva de la duración recibida (decimal, se suma; clamp en server).
  immobilizeDurationReduction?: number; // ej 0.2 = -20% de duración del inmovilizar recibido
  paralyzeDurationReduction?: number; // ej 0.2 = -20% de duración del paralizar recibido


  // Flags (valores booleanos, pero usando number para compatibilidad con el sistema actual)
  // Si el valor es > 0, el flag está activo
  noWalkNoise?: number; // No emitir ruido al caminar (1 = activo, 0 o undefined = inactivo)
  noFootsteps?: number; // No dejar huellas visuales al caminar (1 = activo, 0 o undefined = inactivo)
  noSpellWords?: number; // No emitir palabras mágicas al castear (1 = activo, 0 o undefined = inactivo)
  noStaminaCostPhysical?: number; // No consumir stamina al atacar físicamente (1 = activo, 0 o undefined = inactivo)
  noShield?: number; // No puede equipar escudo (1 = activo). Fuente: keystone Enano "Furia sin Guardia".
  noMeditate?: number; // No puede meditar (1 = activo). Fuente: keystone Gnomo "Trance Perpetuo".
  // Regeneración periódica de maná: cada PERIODIC_MANA_REGEN_INTERVAL_MS restaura
  // este % del maná máximo (decimal, 0.15 = 15%). Fuente: keystone Gnomo "Trance Perpetuo".
  periodicManaRegenPercent?: number;
  
  // Modificadores para invocaciones (summoned entities). Los lee SummonSystem al
  // armar el buff que aplica a cada NPC invocado. Ver docs/SUMMONS.md.
  summonHpBonus?: number; // Bonus de HP para invocaciones (ej: +50 HP)
  summonAttackSpeedBonus?: number; // Bonus de velocidad de ataque para invocaciones (ej: +0.2 = +20%)
  summonPhysicalDamageBonus?: number; // Bonus de daño físico para invocaciones (ej: +5-10 daño)
  summonMagicDamageBonus?: number; // Bonus de daño mágico para invocaciones (ej: +5-10 daño)
  summonCritChance?: number; // Chance de crítico de las invocaciones (decimal, ej: 0.1 = +10%)
  summonCritMultiplier?: number; // Multiplicador de crítico de las invocaciones (decimal, ej: 0.2 = +20%)
  summonResurrectionSpeed?: number; // Reduce el tiempo de resurrección de summons permanentes (decimal, ej: 0.3 = -30%)
  // Aumenta el LÍMITE de invocaciones simultáneas del jugador (aditivo sobre el base
  // SUMMON_BASE_LIMIT). Afijo rolleable con 3 tiers (+1/+2/+3). Ver docs/SUMMONS.md.
  summonLimit?: number;

  /**
   * Magic Find — porcentaje que aumenta la chance de drops RARE/EPIC al matar
   * un mob. Decimal: 0.1 = +10%. Se acumula sumando este modifier de todos los
   * items equipados del killer, y se aplica al `rollRarity()` del drop.
   */
  magicFind?: number;

  // ─── Modificadores ÚNICOS ────────────────────────────────────────────────
  // Sólo viven en items `unique: true`, con valor FIJO (flag 1). NO están en el
  // pool de afijos, así que NUNCA rolean ni tienen tiers. Registro + display en
  // `shared/data/uniqueModifiers.ts`. Flags: > 0 = activo.

  /** Revela en pantalla el nombre de los jugadores muertos (almas). */
  seeDeadNames?: number;
  /** Habilita leer y escribir chat con los muertos (puente vivo↔muerto). */
  talkToDead?: number;
  /** Todos los golpes melee son críticos: ignora el roll de critChance. */
  alwaysCrit?: number;
  /** Mientras esté equipado, el portador no puede lanzar hechizos (silenciado). */
  selfSilence?: number;
  /** Solo permite UN aura activa a la vez, pero duplica el efecto del aura activa. */
  auraSingularity?: number;
  /** Inmoviliza al objetivo en TODOS los golpes melee (chance 1, duración fija). */
  immobilizeEveryHit?: number;
  /** Inmunidad total: no podés ser inmovilizado ni paralizado (no perdés movilidad). */
  impairImmunity?: number;
  /** Podés ver a los jugadores invisibles (revela su gráfico y nombre). Ver docs/INVISIBILITY.md. */
  seeInvisible?: number;
  /** Tu invisibilidad no expira nunca por tiempo (queda permanente hasta romperla). */
  invisibilityNeverExpires?: number;
  /** Estando invisible, no dejás huella ni hacés ruido al caminar (noFootsteps + noWalkNoise). */
  silentWhileInvisible?: number;
  /** Tus hechizos no tienen distancia mínima ni máxima: se lanzan desde cualquier rango. */
  unboundSpellRange?: number;
  /** Habilita el 3er modo de summons (agresivo) en el ciclo de la tecla de modo. */
  summonAggressiveMode?: number;
  /** Tus invocaciones ganan mucha velocidad de ataque pero NO pueden dar críticos. */
  summonFrenzy?: number;
  /** Tus invocaciones atacan muy lento pero su daño está enormemente aumentado. */
  summonExecutioner?: number;
  /** El proceso de invocación invoca +1 criatura. */
  summonPackLeader?: number;
  /**
   * FIJA el límite de invocaciones simultáneas a este número EXACTO (ignora el base y
   * los mods aditivos `summonLimit`). Único de amuleto (valor 5). Ver docs/SUMMONS.md.
   */
  summonLimitFixed?: number;
  /**
   * Podés BLOQUEAR (blockChance) golpes que vengan de cualquier dirección, no sólo de
   * frente. Sin este flag, el bloqueo sólo mitiga golpes frontales (ver
   * BaseEntity.calculateDamage). Flag (>0 = activo). Ver docs/COMBAT_SYSTEMS.md.
   */
  blockOmnidirectional?: number;
  /**
   * Cada golpe melee que conecta EMPUJA al objetivo esta cantidad de tiles hacia atrás
   * (knockback). Reusa el desplazamiento validado por tile libre (GameRoom.handleAttackMessage).
   * 0/undefined = sin empuje. Ver docs/COMBAT_SYSTEMS.md.
   */
  knockbackOnHitTiles?: number;
  /**
   * Habilita el CONTROL de reparto de XP del party: mientras algún miembro vivo y
   * presente porte esta habilidad (amuleto o keystone), puede fijar el % de XP que
   * recibe cada miembro desde la ventana de party (cada uno ≤75%, la suma == 100).
   * Flag (>0 = activo). Lo consume PartyManager.resolveKillRecipients (server). Ver
   * docs/PARTY_SYSTEM.md.
   */
  partyXpControl?: number;
}

/**
 * Componente de un afijo rolled. Cada afijo en una instancia es una lista
 * de componentes; típicamente 1, pero un EPIC puede tener 2 cuando el RNG le
 * toca el mismo modifier key dos veces (en ese caso los componentes deben
 * tener tiers distintos).
 */
export interface RolledAffixComponent {
  /** Valor numérico rolled de este componente (lo que entra al stat sheet). */
  v: number;
  /**
   * Tier para display y economía. Valores válidos: 1, 2, 3.
   * Loose como `number` para fricción cero en boundaries (DB JSON, wire).
   * El roller siempre produce 1/2/3.
   */
  t: number;
}

/**
 * Un afijo rolled puede tener 1 o más componentes. Cuando hay > 1, todos
 * comparten modifier key pero NUNCA repiten tier (por construcción en
 * `rollAffixes`).
 */
export type RolledAffix = RolledAffixComponent[];

/**
 * Mapa de modifier key → componentes rolled de ese afijo. Usado en
 * `ItemInstanceRoll` y persistido en `character_inventory.rolled_affixes`.
 */
export type RolledAffixes = Record<string, RolledAffix>;

/**
 * Configuración de VFX (partículas) asociados a un item.
 * Procesado client-side puro — el server NO simula partículas.
 * Los fxId apuntan al catálogo central `shared/data/particleEffects.json`.
 */
export interface ItemVfxConfig {
  /**
   * Emitters persistentes mientras el item esté equipado en algún slot.
   * El cliente arranca/para via diff cuando el slot cambia.
   * `slot` debe ser uno de los EntityAnchorSlot reconocidos por
   * AnchorResolver: 'weapon_tip', 'weapon_hilt', 'shield_front',
   * 'helm_top', 'center', 'feet', 'head', 'above'.
   */
  equipped?: Array<{ fxId: string; slot: string }>;
  /** FX sobre el item en el piso (drop pre-pickup). */
  ground?: string;
  /** Burst one-shot al swing/attack (F6 sub-emitters lo refinará). */
  onSwing?: string;
}

/**
 * Interfaz base para todos los items del juego.
 *
 * NOTA: el campo `rarity` desapareció del template. La rareza ahora se deriva
 * (LEGENDARY si `unique: true`, SET si `setId`) o se rolea en runtime al crear
 * instancia (si `affixable: true`). Para items no rolleables (consumibles,
 * materiales, quest items) la rareza es COMMON por default.
 */
export interface Item {
  id: string;
  name: string;
  sellable: boolean;
  icon: string;
  animation?: string;
  /**
   * Tipo de item (equipment, consumable, quest, material)
   */
  type?: string;
  /**
   * Subtipo del item (ej: para weapons: dagger, sword, staff, etc.)
   * Solo aplica si type es 'weapon' o según el tipo de item
   */
  subtype?: string;
  /**
   * Si es true, el item es único y no se puede apilar (cada unidad ocupa 1 slot).
   * Marca el item como rareza LEGENDARY en runtime.
   */
  unique?: boolean;
  /**
   * Si es true, este template puede dropear con rareza COMMON/RARE/EPIC y rolear
   * afijos aleatorios al crear instancia. Si es false o undefined, la instancia
   * es siempre COMMON sin afijos rolled.
   * No tiene efecto si el item tiene `unique: true` o `setId` (rareza fija).
   */
  affixable?: boolean;
  /**
   * Texto descriptivo / flavor text. Se renderiza en el detalle del inventario
   * si está presente. Pensado para items legendarios y únicos.
   */
  description?: string;
  /**
   * Sólo aplica a items con `slot === 'weapon'`. Indica si es de una o dos manos.
   * No se infiere del subtipo: un mismo subtipo (ej: 'axe') puede tener variantes
   * 1H y 2H.
   */
  weaponHand?: 'one' | 'two';
  /**
   * Radio de ataque del arma (melee direccional). Intrínsecos del arma: NO se
   * suman entre items. Los bonos acumulables viven en `modifiers.attackRange*`.
   * Sin estos campos, el arma usa la base unarmed. Ver docs/COMBAT_SYSTEMS.md.
   */
  attackWidth?: number; // ancho impar ≥1
  attackLength?: number; // largo 1..5
  attackBack?: number; // 0..1 tiles atrás
  attackStaminaCost?: number; // stamina por golpe (fallback: base unarmed)
  /**
   * Sonido de golpe propio del arma (id de `SOUNDS` en shared/data/sounds.ts). Override del
   * default melee/ranged de su categoría. La mayoría de las armas NO lo define. Ver
   * docs/SOUND_AND_MUSIC.md.
   */
  hitSound?: string;
  /**
   * Sonido de disparo de la munición/flecha (FUTURO: no hay sistema de flechas todavía). Stub
   * data-driven; sin consumidor real hasta que exista el sistema de proyectiles con munición.
   */
  arrowSound?: string;
  /**
   * Lista de clases que pueden equipar el item. `undefined` o `[]` = cualquier
   * clase. El servidor bloquea el equip si la clase del player no está en la lista.
   * Tipo `string[]` por simplicidad — los valores válidos son `CharacterClassId`.
   */
  requiredClasses?: string[];
  /**
   * Facción requerida para equipar/usar el item ('aresgard' | 'elvaria' | 'rebel').
   * `undefined` = cualquier facción (incluido newbie). El server bloquea el equip
   * si la facción del player no coincide. Ver docs/FACTIONS.md. (Hoy ningún item
   * lo usa — campo listo para el futuro.)
   */
  requiredFaction?: string;
  /**
   * Límite de unidades por stack (solo aplica si unique=false).
   * Por defecto 99.
   */
  maxStack?: number;
  modifiers: ItemModifiers;
  slot?: string; // Slot de equipamiento si aplica
  // IDs de sprites visuales (para equipamiento)
  bodyId?: number;   // ID del sprite de body (ej: 11 usa body11.png)
  weaponId?: number; // ID del sprite de weapon (ej: 1 usa weapon1.png)
  shieldId?: number; // ID del sprite de shield (ej: 1 usa shield1.png)
  helmId?: number;   // ID del sprite de casco (se dibuja encima de la cabeza, en /assets/entities/head/headX.png)
  bootId?: number;   // ID del sprite de botas (en /assets/boot/bootX.png)
  headId?: number;   // ID del sprite de head (para la cabeza del personaje, no cascos)
  /**
   * ID del set al que pertenece este item (opcional).
   * Si está set, marca el item como rareza SET en runtime y aporta a los bonos
   * por piezas equipadas (ver SET_BONUSES).
   */
  setId?: string;
  /**
   * F5 — VFX (partículas) asociados al item. Client-side puro, sin gameplay
   * impact. Permite definir emitters mientras está equipado (`equipped`),
   * sobre el item en el piso (`ground`), o al hacer swing (`onSwing`).
   */
  vfx?: ItemVfxConfig;

  // ─── INVOCACIONES por item (ver docs/SUMMONS.md) ─────────────────────────
  /**
   * Perfil de summon (catálogo `shared/data/summonProfiles.ts`) que este item
   * INVOCA mientras esté EQUIPADO. Aparece al equipar, se disipa al desequipar
   * (como un aura toggle "gratis" atada al item). Típico de amuletos de invocador.
   */
  grantsSummonProfileId?: string;
  /**
   * Hechizo (catálogo `shared/data/spells.ts`) que este item OTORGA mientras esté
   * EQUIPADO. Al equipar se aprende y se coloca en la hotbar; al desequipar se olvida
   * y se saca de la hotbar. El hechizo debe ser `grantOnly` (excluido del auto-learn),
   * así es realmente temporal (sólo lo tenés con el item puesto). Ver docs/SPELLS.md.
   */
  grantsSpellId?: string;
  /**
   * Perfil de summon que este item invoca al USARLO (tecla Usar / click "Usar").
   * Requiere que el item esté equipado (accesorio) o en el inventario (consumible).
   * El uso respeta `useCooldownMs` vía CooldownSystem.
   */
  useSummonProfileId?: string;
  /** Cooldown (ms) entre usos de `useSummonProfileId`. Default 0 (sin cooldown). */
  useCooldownMs?: number;
  /**
   * Si true, USAR el item consume 1 unidad del stack (consumible: pociones). Si
   * false/undefined, el item persiste (accesorio usable con cooldown).
   */
  consumeOnUse?: boolean;

  /**
   * COMIDA — al USAR el item (comer), aplica un `TemporaryBuff` "Bien Alimentado"
   * con estos `modifiers` por `durationMs`. Uno activo por vez (refresh: comer de
   * nuevo reemplaza el buff previo). El item se consume siempre (1 unidad). El buff
   * NO cura al instante: lleva tasas/porcentajes (regen, skillXpBonus,
   * toolBreakReduction, etc.). Lo maneja EquipmentRoomHandlers.handleUseItemMessage.
   * Ver docs/FISHING_SKILL.md / plan de Comida.
   */
  foodBuff?: {
    /** Nombre para el HUD del buff (ej. "Bien Alimentado: Salmón asado"). */
    name: string;
    /** Modificadores que aplica mientras dura. */
    modifiers: ItemModifiers;
    /** Duración del buff en ms. */
    durationMs: number;
  };
}

/**
 * Datos rolled específicos de una instancia de item.
 * Se generan al crear la instancia (drop, spawn) y se persisten en BD.
 *
 * - `rolledRarity` es siempre el valor definitivo de la instancia (incluye
 *   LEGENDARY/SET cuando aplica), no solo el resultado del roll.
 * - `rolledAffixes` solo tiene entradas para RARE/EPIC (1 o 2 afijos). Para
 *   COMMON/LEGENDARY/SET viene vacío `{}`.
 */
export interface ItemInstanceRoll {
  rolledRarity: number; // ItemRarity
  rolledAffixes: RolledAffixes;
}

/**
 * Tipo de item
 */
export enum ItemType {
  EQUIPMENT = 'equipment',
  CONSUMABLE = 'consumable',
  QUEST = 'quest',
  MATERIAL = 'material',
}

/**
 * Subtipo de arma (solo aplica si type es 'weapon')
 */
export enum WeaponSubtype {
  DAGGER = 'dagger',
  AXE = 'axe',
  MACE = 'mace',
  SWORD = 'sword',
  STAFF = 'staff',
  WAND = 'wand',
  KNUCKLES = 'knuckles',
  BOW = 'bow',
  CROSSBOW = 'crossbow',
  THROWABLE = 'throwable',
}

/**
 * Subtipo de armadura (aplica para body, head, shield, boots)
 */
export enum ArmorSubtype {
  HEAVY = 'heavy',
  LIGHT = 'light',
  CLOTH = 'cloth',
}

/**
 * Buff temporal que puede aplicarse a una entidad
 */
export interface TemporaryBuff {
  id: string; // ID único del buff
  name: string; // Nombre del buff (para UI)
  modifiers: ItemModifiers; // Modificadores que aplica
  expiresAt: number; // Timestamp de expiración (Date.now() + duración)
  source?: string; // Fuente del buff (ej: 'item', 'spell', 'consumable', 'food')
  /**
   * Ícono para el HUD de buffs. Cuando el buff no matchea ningún spell (ej. comida),
   * `syncActiveBuffsToClients` cae a este ícono antes que a `item_icon_noicon`.
   */
  icon?: string;
  /**
   * Marca explícita de "debuff" (efecto negativo). Cuando está seteada tiene
   * prioridad sobre la convención de ID (`isDebuffBuffId`). La setean los
   * effects de spell inherentemente negativos (debuff, status_effect), así un
   * slow de aura con prefix arbitrario igual se pinta con borde rojo en el HUD
   * y lo limpia el cleanse. Si es undefined, el tipo se infiere del ID.
   */
  isDebuff?: boolean;
  /**
   * FX persistente (catálogo `fx_*`) a mostrar sobre el portador MIENTRAS el buff
   * está activo. El server lo reconcilia al canal `auraFxIds` de la entidad
   * (visible para todos). Copiado desde `SpellEffectBuff.activeFxId` al aplicar.
   * Ver docs/PARTICLES.md §FX de buffs.
   */
  activeFxId?: string;
}

/**
 * Definición de un set de items
 */
export interface ItemSetDefinition {
  id: string; // ID único del set
  name: string; // Nombre del set
  items: string[]; // IDs de items que forman el set
  bonuses: Array<{
    pieces: number; // Cantidad de piezas necesarias (ej: 2, 3, 4)
    modifiers: ItemModifiers; // Modificadores que se aplican
  }>;
}

